import * as i0 from '@angular/core';
import { Directive, EventEmitter, Output, Input, ViewEncapsulation, Component, ElementRef, ANIMATION_MODULE_TYPE, ViewChild, ViewChildren, Inject, TemplateRef, ContentChild, ContentChildren, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i1 from '@angular/animations';
import { style, animate } from '@angular/animations';
import { FocusMonitor } from '@angular/cdk/a11y';
import * as i1$1 from '@angular/common';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import { Subject, timer, takeUntil, merge, fromEvent } from 'rxjs';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { WindmillTooltipDirective, WindmillTooltipModule } from '@windmill/ng-windmill/tooltip';
import { PortalModule } from '@angular/cdk/portal';

/**
 * To use this component in a module, you can import the `WindmillTabsModule`:
 * @example
 * ```typescript
 * import { WindmillTabsModule } from '@windmill/ng-windmill/tabs';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillTabContentDirective`:
 * @example
 * ```typescript
 * import { WindmillTabContentDirective } from '@windmill/ng-windmill/tabs';
 * ```
 */
class WindmillTabContentDirective {
    template;
    constructor(template) {
        this.template = template;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabContentDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTabContentDirective, isStandalone: true, selector: "[windmill-tab-content]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmill-tab-content]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

/**
 * To use this component in a module, you can import the `WindmillTabsModule`:
 * @example
 * ```typescript
 * import { WindmillTabsModule } from '@windmill/ng-windmill/tabs';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillTabLabelDirective`:
 * @example
 * ```typescript
 * import { WindmillTabLabelDirective } from '@windmill/ng-windmill/tabs';
 * ```
 */
class WindmillTabLabelDirective {
    template;
    constructor(template) {
        this.template = template;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabLabelDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTabLabelDirective, isStandalone: true, selector: "[windmill-tab-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmill-tab-label]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

class WindmillTabGroupHeaderSharedProperties {
    /**
     * The theme for tabs
     */
    theme = 'underlined';
    /**
     * The index of the active tab
     */
    selectedIndex;
    /**
     * The position of the arrows
     */
    alignArrows = 'default';
    /**
     * Whether the body tab panel has animation
     */
    disableContentAnimation = true;
    /**
     * Sets the aria-label attribute for the header tab list
     */
    ariaLabelTabList;
    /**
     * Sets the aria-labelledby attribute which will be forwarded to the header tab list
     */
    ariaLabelledbyTabList;
    /**
     * Sets the aria-description attribute for the header tab list
     */
    ariaDescriptionTabList;
    /**
     * Sets the aria-describedby attribute which will be forwarded to the header tab list
     */
    ariaDescribedbyTabList;
    /**
     * Emits a WindmillTabsFocusedItem object when the user puts focus on any of the tab labels in the header
     */
    focusChange = new EventEmitter();
    /**
     * Emits the new selected tab index when the active tab index changes
     */
    selectedIndexChange = new EventEmitter();
    /**
     * Emits the removed tab index when a tab is removed
     */
    removeTab = new EventEmitter();
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabGroupHeaderSharedProperties, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTabGroupHeaderSharedProperties, isStandalone: true, inputs: { theme: "theme", selectedIndex: "selectedIndex", alignArrows: "alignArrows", disableContentAnimation: "disableContentAnimation", ariaLabelTabList: "ariaLabelTabList", ariaLabelledbyTabList: "ariaLabelledbyTabList", ariaDescriptionTabList: "ariaDescriptionTabList", ariaDescribedbyTabList: "ariaDescribedbyTabList" }, outputs: { focusChange: "focusChange", selectedIndexChange: "selectedIndexChange", removeTab: "removeTab" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabGroupHeaderSharedProperties, decorators: [{
            type: Directive
        }], propDecorators: { theme: [{
                type: Input
            }], selectedIndex: [{
                type: Input
            }], alignArrows: [{
                type: Input
            }], disableContentAnimation: [{
                type: Input
            }], ariaLabelTabList: [{
                type: Input
            }], ariaLabelledbyTabList: [{
                type: Input
            }], ariaDescriptionTabList: [{
                type: Input
            }], ariaDescribedbyTabList: [{
                type: Input
            }], focusChange: [{
                type: Output
            }], selectedIndexChange: [{
                type: Output
            }], removeTab: [{
                type: Output
            }] } });

class WindmillTabBodyComponent {
    /**
     * Whether the body tab panel has padding
     */
    hasPadding = true;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTabBodyComponent, isStandalone: true, selector: "windmill-tab-body", inputs: { hasPadding: "hasPadding" }, ngImport: i0, template: "<div\r\n  class=\"windmill-tab-body\"\r\n  [class.has-padding]=\"hasPadding\">\r\n  <ng-content></ng-content>\r\n</div>\r\n", encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-tab-body', encapsulation: ViewEncapsulation.None, standalone: true, template: "<div\r\n  class=\"windmill-tab-body\"\r\n  [class.has-padding]=\"hasPadding\">\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], propDecorators: { hasPadding: [{
                type: Input
            }] } });

const NEGATIVE_VALUE = -1;
const HALF_VALUE = 2;
const THIRD_PART = 3;
const SCROLL_DELAY = 650;
const SCROLL_INTERVAL = 100;
const LEFT_CLICK = 0;
class WindmillTabHeaderComponent extends WindmillTabGroupHeaderSharedProperties {
    injector;
    changeDetector;
    animationBuilder;
    /**
     * Tabs list
     */
    tabs;
    _headerTabLabels;
    _header;
    _headerTabList;
    _headerGroupButtons;
    focusedTab = null;
    hasAriaHidden = true;
    showArrows = false;
    isLeftArrowDisabled = false;
    isRightArrowDisabled = false;
    _elementRef;
    _focusMonitor;
    _focusOrigin;
    _headerTabsResizeObserver;
    _headerTabsMutationObserver;
    _previousFocusedTab = null;
    _destroyed$ = new Subject();
    _scrollingStopped$ = new Subject();
    _animationMode;
    _slideLeftFactory;
    _slideRightFactory;
    _slideLeftPlayer;
    _slideRightPlayer;
    get hasRightArrowsAlignment() {
        return this.alignArrows === 'right';
    }
    get hasDisabledAttributeLeftArrow() {
        return this.isLeftArrowDisabled ? true : null;
    }
    get hasDisabledAttributeRightArrow() {
        return this.isRightArrowDisabled ? true : null;
    }
    get headerTabsElement() {
        return this._elementRef.nativeElement.getElementsByClassName('header-tab-list')[0];
    }
    get _availableScrollDistance() {
        const viewLength = this._headerTabList.nativeElement.offsetWidth;
        const tabListLength = this._headerTabList.nativeElement.scrollWidth;
        return tabListLength - viewLength;
    }
    get _isSelectedIndexValid() {
        return Boolean(this.tabs &&
            (this.selectedIndex || this.selectedIndex === 0) &&
            this.selectedIndex !== null &&
            this.selectedIndex <= this.tabs?.length - 1);
    }
    get _getSelectedWindmillTabElement() {
        if (this.selectedIndex || this.selectedIndex === 0) {
            const selectedTab = this.tabs.toArray()[this.selectedIndex];
            return selectedTab ? selectedTab.elementRef.nativeElement.querySelector('.windmill-tab') : null;
        }
        return null;
    }
    get _headerTabListWidth() {
        return (this._headerTabList?.nativeElement.getBoundingClientRect().width *
            (this._headerTabList?.nativeElement.scrollWidth / this._headerTabList?.nativeElement.clientWidth));
    }
    get _headerWidth() {
        return this._header?.nativeElement.getBoundingClientRect().width;
    }
    get _headerGroupButtonsWidth() {
        return this._headerGroupButtons?.nativeElement.getBoundingClientRect().width;
    }
    constructor(injector, changeDetector, animationBuilder, animationMode) {
        super();
        this.injector = injector;
        this.changeDetector = changeDetector;
        this.animationBuilder = animationBuilder;
        this._elementRef = this.injector.get(ElementRef);
        this._focusMonitor = this.injector.get(FocusMonitor);
        this._animationMode = animationMode;
    }
    ngOnChanges(changes) {
        if (changes['selectedIndex'] && (this.selectedIndex || this.selectedIndex === 0)) {
            this.setActiveState(this.selectedIndex);
            this.setIndexValue();
            if (this._animationMode !== 'NoopAnimations' && !this.disableContentAnimation) {
                const previousSelectedTabIndex = changes['selectedIndex'].previousValue;
                this._contentAnimationHandler(previousSelectedTabIndex);
            }
        }
    }
    ngAfterContentInit() {
        const selectedTabIndex = this._isSelectedIndexValid ? this.selectedIndex : 0;
        if (selectedTabIndex || selectedTabIndex === 0) {
            this.selectTab(selectedTabIndex, true);
        }
    }
    ngAfterViewInit() {
        this._observeHeaderTabsResize();
        this._observeHeaderTabsMutation();
        if (this.selectedIndex || this.selectedIndex === 0) {
            this.scrollIntoViewToActiveTab(this.selectedIndex);
        }
        this._listenMouseLeaveEvent();
        this._handleFocusMonitor();
    }
    ngOnDestroy() {
        this._destroyed$.next();
        this._scrollingStopped$.next();
        this._headerTabsResizeObserver.disconnect();
        this._headerTabsMutationObserver.disconnect();
        this._focusMonitor.stopMonitoring(this.headerTabsElement);
    }
    selectTab(selectedTabIndex, isOnInit) {
        const isSelectingCurrentActiveTab = this.selectedIndex === selectedTabIndex;
        this.setActiveState(selectedTabIndex);
        this.setIndexValue();
        this._emitSelectedIndexChange(isOnInit);
        this._emitFocusChange(selectedTabIndex, isOnInit, isSelectingCurrentActiveTab);
    }
    onRemoveTab(removedTabIndex) {
        this.removeTab.emit(removedTabIndex);
        this._focusHeaderTab(removedTabIndex === this.tabs?.length - 1 ? removedTabIndex - 1 : removedTabIndex);
    }
    isTabActive(tab) {
        return tab.disabled ? false : tab.active;
    }
    ariaControlsValue(tab) {
        return tab.disabled ? null : tab.id;
    }
    tabShouldHaveCloseButton(tab) {
        return tab.hasCloseButton && !tab.disabled;
    }
    setActiveState(selectedTabIndex) {
        this.tabs?.forEach((tab, i) => {
            tab.active = i === selectedTabIndex;
            if (i === selectedTabIndex) {
                this.selectedIndex = selectedTabIndex;
            }
        });
    }
    setIndexValue() {
        this.tabs?.forEach((tab, i) => {
            tab.index = i;
        });
    }
    onHeaderTabClick(tab, index) {
        if (!tab?.disabled) {
            this.selectTab(index);
            this._scrollIntoViewToTab(index);
        }
    }
    onArrowClick(scrollDirection) {
        this._stopScrollingInterval();
        this._scrollHeaderTabs(scrollDirection);
    }
    onTabListScroll() {
        this._updateDisabledStateForArrows();
    }
    getTabIndexForTab(tab) {
        return tab.active || this.focusedTab === tab ? 0 : NEGATIVE_VALUE;
    }
    getTabIndexForCloseButton(tab) {
        return this.focusedTab === tab ? 0 : NEGATIVE_VALUE;
    }
    hasAriaDisabled(tab) {
        return tab.disabled ? tab.disabled : null;
    }
    isLocked(tab) {
        return tab.isLocked && !tab.disabled;
    }
    isLockedWithTooltip(tab) {
        return this.isLocked(tab) && Boolean(tab.lockedTooltipMessage);
    }
    onArrowMousedown(scrollDirection, event) {
        if (event.button !== LEFT_CLICK) {
            return;
        }
        this._stopScrollingInterval();
        timer(SCROLL_DELAY, SCROLL_INTERVAL)
            .pipe(takeUntil(merge(this._scrollingStopped$, this._destroyed$)))
            .subscribe(() => {
            const { finalPosition } = this._scrollHeaderTabs(scrollDirection);
            if (finalPosition === 0 || finalPosition >= this._availableScrollDistance) {
                this._stopScrollingInterval();
            }
        });
    }
    onHeaderTabFocusin(tab) {
        this.focusedTab = tab;
    }
    onHeaderTabFocusout() {
        if (this.focusedTab) {
            this._previousFocusedTab = this.focusedTab;
        }
        this.focusedTab = null;
    }
    onHeaderTabKeyDown(event, index) {
        this._onArrowsKeyDown(event, index);
        this._onDeleteKeyDown(event, index);
        this._onHomeOrEndKeyDown(event, index);
    }
    updateArrowsAppearance() {
        if (Math.floor(this._headerTabListWidth) > Math.floor(this._headerWidth - this._headerGroupButtonsWidth)) {
            this.showArrows = true;
        }
        else {
            this.showArrows = false;
        }
        this._updateDisabledStateForArrows();
    }
    scrollIntoViewToActiveTab(index) {
        if (this._isIndexValid(index) && this._headerTabLabels?.length) {
            const newPosition = this._getNewPosition(index);
            setTimeout(() => {
                this._headerTabList.nativeElement.scrollTo({ behavior: 'smooth', left: newPosition });
            });
        }
    }
    _handleFocusMonitor() {
        this._focusMonitor.monitor(this.headerTabsElement, true).subscribe(origin => {
            this._focusOrigin = origin;
        });
    }
    _scrollIntoViewToTab(index) {
        this._headerTabList.nativeElement
            .getElementsByClassName('header-tab-label')[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    _scrollHeaderTabs(direction) {
        const viewLength = this._headerTabList.nativeElement.offsetWidth;
        const scrollAmount = ((direction === 'left' ? NEGATIVE_VALUE : 1) * viewLength) / THIRD_PART;
        const newPosition = this._headerTabList.nativeElement.scrollLeft + scrollAmount;
        this._headerTabList.nativeElement.scrollTo({ behavior: 'smooth', left: newPosition });
        return { finalPosition: newPosition };
    }
    _stopScrollingInterval() {
        this._scrollingStopped$.next();
    }
    _contentAnimationHandler(previousSelectedTabIndex) {
        if (this.selectedIndex || this.selectedIndex === 0) {
            if (previousSelectedTabIndex < this.selectedIndex) {
                this._buildAndPlayContentAnimation('left');
            }
            else if (previousSelectedTabIndex > this.selectedIndex) {
                this._buildAndPlayContentAnimation('right');
            }
        }
    }
    _buildAndPlayContentAnimation(animationName) {
        const selectedWindmillTabElement = this._getSelectedWindmillTabElement;
        if (!selectedWindmillTabElement) {
            return;
        }
        switch (animationName) {
            case 'left':
                this._slideLeftFactory = this.animationBuilder.build(this._slideLeft());
                this._slideLeftPlayer = this._slideLeftFactory.create(selectedWindmillTabElement);
                this._slideLeftPlayer.play();
                break;
            case 'right':
                this._slideRightFactory = this.animationBuilder.build(this._slideRight());
                this._slideRightPlayer = this._slideRightFactory.create(selectedWindmillTabElement);
                this._slideRightPlayer.play();
                break;
            default:
                break;
        }
    }
    _slideLeft() {
        return [style({ transform: 'translateX(100%)' }), animate('400ms', style({ transform: 'translateX(0)' }))];
    }
    _slideRight() {
        return [style({ transform: 'translateX(-100%)' }), animate('400ms', style({ transform: 'translateX(0)' }))];
    }
    _observeHeaderTabsResize() {
        this._headerTabsResizeObserver = new ResizeObserver(() => {
            this._updateHeaderTabs();
        });
        this._headerTabsResizeObserver.observe(this.headerTabsElement);
    }
    _observeHeaderTabsMutation() {
        this._headerTabsMutationObserver = new MutationObserver(() => {
            this._updateHeaderTabs();
        });
        this._headerTabsMutationObserver.observe(this.headerTabsElement, {
            subtree: true,
            characterData: true
        });
    }
    _updateHeaderTabs() {
        this.updateArrowsAppearance();
        if (this.selectedIndex || this.selectedIndex === 0) {
            this.scrollIntoViewToActiveTab(this.selectedIndex);
        }
        this.changeDetector.detectChanges();
    }
    _getNewPosition(newSelectedIndex) {
        let position = 0;
        for (const tab of this._headerTabLabels) {
            position += tab.nativeElement.clientWidth;
            const activeTab = this._headerTabLabels?.toArray()[newSelectedIndex]?.nativeElement;
            if (tab.nativeElement === activeTab) {
                break;
            }
        }
        position -= this._headerTabList.nativeElement.clientWidth / HALF_VALUE;
        return position;
    }
    _isIndexValid(index) {
        return Boolean(this.tabs && (index || index === 0) && index !== null && index <= this.tabs?.length - 1);
    }
    _emitSelectedIndexChange(isOnInit) {
        if ((this.selectedIndex || this.selectedIndex === 0) && !isOnInit) {
            this.selectedIndexChange.emit(this.selectedIndex);
        }
    }
    _emitFocusChange(selectedTabIndex, isOnInit, isSelectingCurrentActiveTab) {
        const tab = this.tabs?.toArray()[selectedTabIndex];
        if (!isOnInit && this._previousFocusedTab !== tab && selectedTabIndex >= 0) {
            this._previousFocusedTab = tab;
            const index = this.tabs?.toArray().indexOf(tab);
            if (!isSelectingCurrentActiveTab && this._focusOrigin !== 'keyboard') {
                this.focusChange.emit({ index, tab });
            }
        }
    }
    _updateDisabledStateForArrows() {
        const headerTabListElement = this._headerTabList.nativeElement;
        this.isLeftArrowDisabled = headerTabListElement.scrollLeft === 0;
        const rightPosition = headerTabListElement.scrollWidth - headerTabListElement.scrollLeft;
        this.isRightArrowDisabled =
            Math.floor(rightPosition) === headerTabListElement.clientWidth ||
                Math.ceil(rightPosition) === headerTabListElement.clientWidth;
    }
    _focusHeaderTab(index) {
        setTimeout(() => {
            const focusedTabComponent = this.tabs?.toArray()[index];
            this._headerTabLabels.toArray()[index]?.nativeElement?.focus();
            if (this._previousFocusedTab !== focusedTabComponent) {
                this.focusChange.emit({ index, tab: focusedTabComponent });
            }
        });
    }
    _onArrowsKeyDown(event, index) {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key) && this.tabs?.length === 1) {
            this._previousFocusedTab = this.tabs?.toArray()[index];
        }
        this._onArrowRightOrDownKeyDown(event, index);
        this._onArrowLeftOrUpKeyDown(event, index);
    }
    _onArrowRightOrDownKeyDown(event, index) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (index === this._headerTabLabels.length - 1) {
                this._focusHeaderTab(0);
            }
            else {
                this._focusHeaderTab(index + 1);
                this.scrollIntoViewToActiveTab(index);
            }
        }
    }
    _onArrowLeftOrUpKeyDown(event, index) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (index === 0) {
                this._focusHeaderTab(this._headerTabLabels.length - 1);
            }
            else {
                this._focusHeaderTab(index - 1);
                this.scrollIntoViewToActiveTab(index - 1);
            }
        }
    }
    _onDeleteKeyDown(event, index) {
        if (event.key === 'Delete' && this.tabs?.toArray()[index].hasCloseButton) {
            this.onRemoveTab(index);
        }
    }
    _onHomeOrEndKeyDown(event, index) {
        this._onHomeKeyDown(event, index);
        this._onEndKeyDown(event, index);
    }
    _onHomeKeyDown(event, index) {
        if (event.key === 'Home') {
            event.preventDefault();
            if (index !== 0) {
                this._focusHeaderTab(0);
            }
        }
    }
    _onEndKeyDown(event, index) {
        if (event.key === 'End') {
            event.preventDefault();
            if (index !== this._headerTabLabels.length - 1) {
                this._focusHeaderTab(this._headerTabLabels.length - 1);
            }
        }
    }
    _listenMouseLeaveEvent() {
        fromEvent(this._headerTabList.nativeElement, 'mouseleave')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
            this._stopScrollingInterval();
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabHeaderComponent, deps: [{ token: i0.Injector }, { token: i0.ChangeDetectorRef }, { token: i1.AnimationBuilder }, { token: ANIMATION_MODULE_TYPE }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillTabHeaderComponent, isStandalone: true, selector: "windmill-tab-header", inputs: { tabs: "tabs" }, viewQueries: [{ propertyName: "_header", first: true, predicate: ["header"], descendants: true }, { propertyName: "_headerTabList", first: true, predicate: ["headerTabList"], descendants: true }, { propertyName: "_headerGroupButtons", first: true, predicate: ["headerGroupButtons"], descendants: true }, { propertyName: "_headerTabLabels", predicate: ["headerTabLabelButton"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div\r\n  #header\r\n  class=\"windmill-tab-header\"\r\n  [class.underlined-theme]=\"theme === 'underlined'\"\r\n  [class.filled-theme]=\"theme === 'filled'\">\r\n  @if (!hasRightArrowsAlignment && showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"leftArrow\"></ng-container>\r\n  }\r\n  <div\r\n    #headerTabList\r\n    class=\"header-tab-list\"\r\n    role=\"tablist\"\r\n    [attr.aria-label]=\"ariaLabelTabList\"\r\n    [attr.aria-labelledby]=\"ariaLabelledbyTabList\"\r\n    [attr.aria-description]=\"ariaDescriptionTabList\"\r\n    [attr.aria-describedby]=\"ariaDescribedbyTabList\"\r\n    (scroll)=\"onTabListScroll()\">\r\n    @for (tab of tabs; track $index) {\r\n      <div\r\n        class=\"header-tab\"\r\n        [class.is-active]=\"isTabActive(tab)\"\r\n        [class.is-disabled]=\"tab.disabled\"\r\n        [class.has-close-button]=\"tabShouldHaveCloseButton(tab)\">\r\n        @if (!isLockedWithTooltip(tab)) {\r\n          <button\r\n            #headerTabLabelButton\r\n            class=\"header-tab-label\"\r\n            role=\"tab\"\r\n            [attr.tabindex]=\"getTabIndexForTab(tab)\"\r\n            [attr.id]=\"tab.ariaLabelledbyTabPanel\"\r\n            [attr.aria-controls]=\"ariaControlsValue(tab)\"\r\n            [attr.aria-selected]=\"isTabActive(tab)\"\r\n            [attr.aria-disabled]=\"hasAriaDisabled(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabel\"\r\n            [attr.aria-labelledby]=\"tab.ariaLabelledby\"\r\n            [attr.aria-description]=\"tab.ariaDescription\"\r\n            [attr.aria-describedby]=\"tab.ariaDescribedby\"\r\n            (click)=\"onHeaderTabClick(tab, $index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            @if (tab.labelDirective) {\r\n              <ng-container *ngTemplateOutlet=\"tab.labelDirective.template\"></ng-container>\r\n            } @else {\r\n              {{ tab.label }}\r\n            }\r\n            @if (isLocked(tab)) {\r\n              <centric-icon2\r\n                [svgIcon]=\"'lock_b'\"\r\n                [size]=\"'16'\"></centric-icon2>\r\n            }\r\n          </button>\r\n        }\r\n        @if (isLockedWithTooltip(tab)) {\r\n          <button\r\n            #headerTabLabelButton\r\n            class=\"header-tab-label\"\r\n            role=\"tab\"\r\n            [attr.tabindex]=\"getTabIndexForTab(tab)\"\r\n            [attr.id]=\"tab.ariaLabelledbyTabPanel\"\r\n            [attr.aria-controls]=\"ariaControlsValue(tab)\"\r\n            [attr.aria-selected]=\"isTabActive(tab)\"\r\n            [attr.aria-disabled]=\"hasAriaDisabled(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabel\"\r\n            [attr.aria-labelledby]=\"tab.ariaLabelledby\"\r\n            [attr.aria-description]=\"tab.ariaDescription\"\r\n            [attr.aria-describedby]=\"tab.ariaDescribedby\"\r\n            windmill-tooltip\r\n            [tooltipPosition]=\"'above'\"\r\n            [tooltipText]=\"tab.lockedTooltipMessage ?? ''\"\r\n            [tooltipColor]=\"tab.lockedTooltipColor\"\r\n            (click)=\"onHeaderTabClick(tab, $index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            @if (tab.labelDirective) {\r\n              <ng-container *ngTemplateOutlet=\"tab.labelDirective.template\"></ng-container>\r\n            } @else {\r\n              {{ tab.label }}\r\n            }\r\n            <centric-icon2\r\n              [svgIcon]=\"'lock_b'\"\r\n              [size]=\"'16'\"></centric-icon2>\r\n          </button>\r\n        }\r\n\r\n        @if (tabShouldHaveCloseButton(tab)) {\r\n          <button\r\n            class=\"windmill-close-tab-button\"\r\n            [attr.tabindex]=\"getTabIndexForCloseButton(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabelCloseTabButton\"\r\n            (click)=\"onRemoveTab($index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_r'\"\r\n              [size]=\"'16'\"></centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n    }\r\n  </div>\r\n  @if (hasRightArrowsAlignment && showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"leftArrow\"></ng-container>\r\n  }\r\n  @if (showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"rightArrow\"></ng-container>\r\n  }\r\n  <div\r\n    #headerGroupButtons\r\n    class=\"windmill-tab-group-buttons\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n\r\n<ng-template #leftArrow>\r\n  <button\r\n    class=\"left-navigation-arrow\"\r\n    [class.is-disabled]=\"isLeftArrowDisabled\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.disabled]=\"hasDisabledAttributeLeftArrow\"\r\n    [attr.aria-hidden]=\"hasAriaHidden\"\r\n    (mousedown)=\"onArrowMousedown('left', $event)\"\r\n    (click)=\"onArrowClick('left')\">\r\n    <centric-icon2\r\n      [svgIcon]=\"'chevron-small-left_r'\"\r\n      [size]=\"'26'\"></centric-icon2>\r\n  </button>\r\n</ng-template>\r\n\r\n<ng-template #rightArrow>\r\n  <button\r\n    class=\"right-navigation-arrow\"\r\n    [class.is-disabled]=\"isRightArrowDisabled\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.disabled]=\"hasDisabledAttributeRightArrow\"\r\n    [attr.aria-hidden]=\"hasAriaHidden\"\r\n    (mousedown)=\"onArrowMousedown('right', $event)\"\r\n    (click)=\"onArrowClick('right')\">\r\n    <centric-icon2\r\n      [svgIcon]=\"'chevron-small-right_r'\"\r\n      [size]=\"'26'\"></centric-icon2>\r\n  </button>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "directive", type: WindmillTooltipDirective, selector: "[windmill-tooltip]", inputs: ["tooltipText", "tooltipPosition", "tooltipShowDelay", "tooltipHideDelay", "tooltipClassName", "tooltipColor", "isTooltipDisabled"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-tab-header', encapsulation: ViewEncapsulation.None, imports: [NgTemplateOutlet, CentricIcon2Component, WindmillTooltipDirective], standalone: true, template: "<div\r\n  #header\r\n  class=\"windmill-tab-header\"\r\n  [class.underlined-theme]=\"theme === 'underlined'\"\r\n  [class.filled-theme]=\"theme === 'filled'\">\r\n  @if (!hasRightArrowsAlignment && showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"leftArrow\"></ng-container>\r\n  }\r\n  <div\r\n    #headerTabList\r\n    class=\"header-tab-list\"\r\n    role=\"tablist\"\r\n    [attr.aria-label]=\"ariaLabelTabList\"\r\n    [attr.aria-labelledby]=\"ariaLabelledbyTabList\"\r\n    [attr.aria-description]=\"ariaDescriptionTabList\"\r\n    [attr.aria-describedby]=\"ariaDescribedbyTabList\"\r\n    (scroll)=\"onTabListScroll()\">\r\n    @for (tab of tabs; track $index) {\r\n      <div\r\n        class=\"header-tab\"\r\n        [class.is-active]=\"isTabActive(tab)\"\r\n        [class.is-disabled]=\"tab.disabled\"\r\n        [class.has-close-button]=\"tabShouldHaveCloseButton(tab)\">\r\n        @if (!isLockedWithTooltip(tab)) {\r\n          <button\r\n            #headerTabLabelButton\r\n            class=\"header-tab-label\"\r\n            role=\"tab\"\r\n            [attr.tabindex]=\"getTabIndexForTab(tab)\"\r\n            [attr.id]=\"tab.ariaLabelledbyTabPanel\"\r\n            [attr.aria-controls]=\"ariaControlsValue(tab)\"\r\n            [attr.aria-selected]=\"isTabActive(tab)\"\r\n            [attr.aria-disabled]=\"hasAriaDisabled(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabel\"\r\n            [attr.aria-labelledby]=\"tab.ariaLabelledby\"\r\n            [attr.aria-description]=\"tab.ariaDescription\"\r\n            [attr.aria-describedby]=\"tab.ariaDescribedby\"\r\n            (click)=\"onHeaderTabClick(tab, $index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            @if (tab.labelDirective) {\r\n              <ng-container *ngTemplateOutlet=\"tab.labelDirective.template\"></ng-container>\r\n            } @else {\r\n              {{ tab.label }}\r\n            }\r\n            @if (isLocked(tab)) {\r\n              <centric-icon2\r\n                [svgIcon]=\"'lock_b'\"\r\n                [size]=\"'16'\"></centric-icon2>\r\n            }\r\n          </button>\r\n        }\r\n        @if (isLockedWithTooltip(tab)) {\r\n          <button\r\n            #headerTabLabelButton\r\n            class=\"header-tab-label\"\r\n            role=\"tab\"\r\n            [attr.tabindex]=\"getTabIndexForTab(tab)\"\r\n            [attr.id]=\"tab.ariaLabelledbyTabPanel\"\r\n            [attr.aria-controls]=\"ariaControlsValue(tab)\"\r\n            [attr.aria-selected]=\"isTabActive(tab)\"\r\n            [attr.aria-disabled]=\"hasAriaDisabled(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabel\"\r\n            [attr.aria-labelledby]=\"tab.ariaLabelledby\"\r\n            [attr.aria-description]=\"tab.ariaDescription\"\r\n            [attr.aria-describedby]=\"tab.ariaDescribedby\"\r\n            windmill-tooltip\r\n            [tooltipPosition]=\"'above'\"\r\n            [tooltipText]=\"tab.lockedTooltipMessage ?? ''\"\r\n            [tooltipColor]=\"tab.lockedTooltipColor\"\r\n            (click)=\"onHeaderTabClick(tab, $index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            @if (tab.labelDirective) {\r\n              <ng-container *ngTemplateOutlet=\"tab.labelDirective.template\"></ng-container>\r\n            } @else {\r\n              {{ tab.label }}\r\n            }\r\n            <centric-icon2\r\n              [svgIcon]=\"'lock_b'\"\r\n              [size]=\"'16'\"></centric-icon2>\r\n          </button>\r\n        }\r\n\r\n        @if (tabShouldHaveCloseButton(tab)) {\r\n          <button\r\n            class=\"windmill-close-tab-button\"\r\n            [attr.tabindex]=\"getTabIndexForCloseButton(tab)\"\r\n            [attr.aria-label]=\"tab.ariaLabelCloseTabButton\"\r\n            (click)=\"onRemoveTab($index)\"\r\n            (focusin)=\"onHeaderTabFocusin(tab)\"\r\n            (focusout)=\"onHeaderTabFocusout()\"\r\n            (keydown)=\"onHeaderTabKeyDown($event, $index)\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_r'\"\r\n              [size]=\"'16'\"></centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n    }\r\n  </div>\r\n  @if (hasRightArrowsAlignment && showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"leftArrow\"></ng-container>\r\n  }\r\n  @if (showArrows) {\r\n    <ng-container *ngTemplateOutlet=\"rightArrow\"></ng-container>\r\n  }\r\n  <div\r\n    #headerGroupButtons\r\n    class=\"windmill-tab-group-buttons\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n\r\n<ng-template #leftArrow>\r\n  <button\r\n    class=\"left-navigation-arrow\"\r\n    [class.is-disabled]=\"isLeftArrowDisabled\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.disabled]=\"hasDisabledAttributeLeftArrow\"\r\n    [attr.aria-hidden]=\"hasAriaHidden\"\r\n    (mousedown)=\"onArrowMousedown('left', $event)\"\r\n    (click)=\"onArrowClick('left')\">\r\n    <centric-icon2\r\n      [svgIcon]=\"'chevron-small-left_r'\"\r\n      [size]=\"'26'\"></centric-icon2>\r\n  </button>\r\n</ng-template>\r\n\r\n<ng-template #rightArrow>\r\n  <button\r\n    class=\"right-navigation-arrow\"\r\n    [class.is-disabled]=\"isRightArrowDisabled\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.disabled]=\"hasDisabledAttributeRightArrow\"\r\n    [attr.aria-hidden]=\"hasAriaHidden\"\r\n    (mousedown)=\"onArrowMousedown('right', $event)\"\r\n    (click)=\"onArrowClick('right')\">\r\n    <centric-icon2\r\n      [svgIcon]=\"'chevron-small-right_r'\"\r\n      [size]=\"'26'\"></centric-icon2>\r\n  </button>\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.ChangeDetectorRef }, { type: i1.AnimationBuilder }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }], propDecorators: { tabs: [{
                type: Input
            }], _headerTabLabels: [{
                type: ViewChildren,
                args: ['headerTabLabelButton']
            }], _header: [{
                type: ViewChild,
                args: ['header']
            }], _headerTabList: [{
                type: ViewChild,
                args: ['headerTabList']
            }], _headerGroupButtons: [{
                type: ViewChild,
                args: ['headerGroupButtons']
            }] } });

/**
 * To use this component in a module, you can import the `WindmillTabsModule`:
 * @example
 * ```typescript
 * import { WindmillTabsModule } from '@windmill/ng-windmill/tabs';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillTabComponent`:
 * @example
 * ```typescript
 * import { WindmillTabComponent } from '@windmill/ng-windmill/tabs';
 * ```
 */
class WindmillTabComponent {
    _elementRef;
    changeDetectorRef;
    /**
     * Label of the tab
     */
    label;
    /**
     * Whether the tab is disabled
     */
    disabled = false;
    /**
     * Whether the tab has close button
     */
    hasCloseButton = false;
    /**
     * Sets the id for body tab panel, which will be forwarded to the aria-controls attribute set on the header tab label button
     */
    id;
    /**
     * If true, a lock icon is displayed on the tab
     */
    isLocked = false;
    /**
     * If the tab is locked, the property sets the tooltip message
     */
    lockedTooltipMessage;
    /**
     * If the tab is locked, the property sets the tooltip color
     */
    get lockedTooltipColor() {
        return this._lockedTooltipColor;
    }
    set lockedTooltipColor(newValue) {
        if (['neutral', 'surface', 'theme'].includes(newValue)) {
            this._lockedTooltipColor = newValue;
        }
        else {
            this._lockedTooltipColor = 'neutral';
        }
    }
    /**
     * Sets the aria-label attribute for the close tab button, when hasCloseButton is set to true
     */
    ariaLabelCloseTabButton;
    /**
     * Sets the aria-label attribute for the header tab label button
     */
    ariaLabel;
    /**
     * Sets the aria-labelledby attribute which will be forwarded to the header tab label button
     */
    ariaLabelledby;
    /**
     * Sets the aria-description attribute for the header tab label button
     */
    ariaDescription;
    /**
     * Sets the aria-describedby attribute which will be forwarded to the header tab label button
     */
    ariaDescribedby;
    /**
     * Sets the aria-labelledby attribute which will be forwarded to the body tab panel, which will be forwarded to the id for header tab label button
     */
    ariaLabelledbyTabPanel;
    get active() {
        return this._active;
    }
    set active(newValue) {
        this._active = newValue;
        this.changeDetectorRef.detectChanges();
    }
    elementRef;
    index = 0;
    contentTemplate;
    labelDirective;
    _active = false;
    _lockedTooltipColor = 'neutral';
    constructor(_elementRef, changeDetectorRef) {
        this._elementRef = _elementRef;
        this.changeDetectorRef = changeDetectorRef;
        this.elementRef = this._elementRef;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillTabComponent, isStandalone: true, selector: "windmill-tab", inputs: { label: "label", disabled: "disabled", hasCloseButton: "hasCloseButton", id: "id", isLocked: "isLocked", lockedTooltipMessage: "lockedTooltipMessage", lockedTooltipColor: "lockedTooltipColor", ariaLabelCloseTabButton: "ariaLabelCloseTabButton", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby", ariaLabelledbyTabPanel: "ariaLabelledbyTabPanel" }, queries: [{ propertyName: "contentTemplate", first: true, predicate: WindmillTabContentDirective, descendants: true, read: (TemplateRef) }, { propertyName: "labelDirective", first: true, predicate: WindmillTabLabelDirective, descendants: true, read: WindmillTabLabelDirective, static: true }], ngImport: i0, template: "@if (active && !disabled) {\r\n  <div\r\n    class=\"windmill-tab\"\r\n    role=\"tabpanel\"\r\n    [attr.id]=\"id\"\r\n    [attr.aria-labelledby]=\"ariaLabelledbyTabPanel\">\r\n    @if (contentTemplate) {\r\n      <ng-container [ngTemplateOutlet]=\"contentTemplate\"></ng-container>\r\n    } @else {\r\n      <ng-content></ng-content>\r\n    }\r\n  </div>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-tab', encapsulation: ViewEncapsulation.None, imports: [CommonModule, NgTemplateOutlet], standalone: true, template: "@if (active && !disabled) {\r\n  <div\r\n    class=\"windmill-tab\"\r\n    role=\"tabpanel\"\r\n    [attr.id]=\"id\"\r\n    [attr.aria-labelledby]=\"ariaLabelledbyTabPanel\">\r\n    @if (contentTemplate) {\r\n      <ng-container [ngTemplateOutlet]=\"contentTemplate\"></ng-container>\r\n    } @else {\r\n      <ng-content></ng-content>\r\n    }\r\n  </div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }], propDecorators: { label: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], id: [{
                type: Input
            }], isLocked: [{
                type: Input
            }], lockedTooltipMessage: [{
                type: Input
            }], lockedTooltipColor: [{
                type: Input
            }], ariaLabelCloseTabButton: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelledbyTabPanel: [{
                type: Input
            }], contentTemplate: [{
                type: ContentChild,
                args: [WindmillTabContentDirective, { read: (TemplateRef) }]
            }], labelDirective: [{
                type: ContentChild,
                args: [WindmillTabLabelDirective, { read: WindmillTabLabelDirective, static: true }]
            }] } });

const FIRST_TAB_INDEX = 0;
const SMALL_PADDING_VALUE = 8;
const DEFAULT_PADDING_VALUE = 16;
/**
 * To use this component in a module, you can import the `WindmillTabsModule`:
 * @example
 * ```typescript
 * import { WindmillTabsModule } from '@windmill/ng-windmill/tabs';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillTabGroupComponent`:
 * @example
 * ```typescript
 * import { WindmillTabGroupComponent } from '@windmill/ng-windmill/tabs';
 * ```
 */
class WindmillTabGroupComponent extends WindmillTabGroupHeaderSharedProperties {
    /**
     * Whether the tabs container has shadow
     */
    shadow = false;
    /**
     * Whether the tabs container has border
     */
    noBorder = false;
    /**
     * Whether the tabs header is separated from the body tab panel
     */
    separatedHeader = false;
    /**
     * The padding between header and content, when separatedHeader is set to true
     */
    separatedHeaderPadding = DEFAULT_PADDING_VALUE;
    /**
     * Whether the body tab panel has padding
     */
    hasPadding = true;
    /**
     * Emits a WindmillTabComponent object when the active tab changes
     */
    selectedTabChange = new EventEmitter();
    tabs;
    tabHeader;
    _previousSelectedTabIndex;
    _tabsChangeSubscription;
    get hasBorderClass() {
        return !this.noBorder && !this.shadow && !this.separatedHeader;
    }
    get hasSmallSeparatedHeaderPadding() {
        return Boolean(this.separatedHeader && this.separatedHeaderPadding === SMALL_PADDING_VALUE);
    }
    get _isSelectedIndexValid() {
        return Boolean(this.tabs &&
            (this.selectedIndex || this.selectedIndex === 0) &&
            this.selectedIndex <= this.tabs.length - 1 &&
            this.selectedIndex >= 0 &&
            this.selectedIndex !== this._previousSelectedTabIndex);
    }
    get _validSelectedIndex() {
        if (this.selectedIndex || this.selectedIndex === 0) {
            if (this.selectedIndex === this.tabs.length) {
                return this.selectedIndex - 1;
            }
            else if (this.selectedIndex >= 0) {
                return this.selectedIndex;
            }
        }
        return FIRST_TAB_INDEX;
    }
    ngOnChanges(changes) {
        if (changes['selectedIndex']) {
            if (changes['selectedIndex'].firstChange) {
                this._updateOnFirstSelectedIndexChange();
            }
            else {
                this._updateOnSelectedIndexChange();
            }
            if (this.selectedIndex || this.selectedIndex === 0) {
                this.tabHeader?.scrollIntoViewToActiveTab(this.selectedIndex);
            }
        }
    }
    ngOnInit() {
        if (this.selectedIndex || this.selectedIndex === 0) {
            this._previousSelectedTabIndex = this.selectedIndex;
        }
        else {
            this.selectedIndex = FIRST_TAB_INDEX;
            this._previousSelectedTabIndex = FIRST_TAB_INDEX;
        }
    }
    ngAfterViewInit() {
        this._subscribeToTabChanges();
    }
    ngOnDestroy() {
        this._tabsChangeSubscription?.unsubscribe();
    }
    onFocusChange(event) {
        this.focusChange.emit(event);
    }
    onSelectedIndexChange(index) {
        if (this.tabs && this.selectedIndex !== index) {
            this.selectedIndex = index;
            this._onIndexChange(index);
        }
    }
    onRemoveTab(removedTabIndex) {
        this.removeTab.emit(removedTabIndex);
        if ((this.selectedIndex || this.selectedIndex === 0) && removedTabIndex < this.selectedIndex) {
            this.selectedIndex -= 1;
        }
    }
    _subscribeToTabChanges() {
        this._tabsChangeSubscription = this.tabs.changes.subscribe(() => {
            this._updateSelectedIndexAndActiveState();
            setTimeout(() => {
                this.tabHeader.updateArrowsAppearance();
            });
        });
    }
    _onIndexChange(index) {
        setTimeout(() => {
            const newIndex = index >= this.tabs?.length ? this.tabs?.length - 1 : index;
            if (index >= this.tabs?.length) {
                this.selectedIndex = newIndex;
            }
            this._previousSelectedTabIndex = newIndex;
            this.selectedIndexChange.emit(newIndex);
            this.selectedTabChange.emit(this.tabs?.toArray()[newIndex]);
        });
    }
    _updateOnFirstSelectedIndexChange() {
        if (this.selectedIndex || this.selectedIndex === 0) {
            this._onIndexChange(this.selectedIndex);
        }
    }
    _updateOnSelectedIndexChange() {
        if ((this.selectedIndex || this.selectedIndex === 0) && this._isSelectedIndexValid) {
            this._onIndexChange(this.selectedIndex);
        }
        else {
            this.selectedIndex = this._previousSelectedTabIndex;
        }
    }
    _updateSelectedIndexAndActiveState() {
        if (this.selectedIndex || this.selectedIndex === 0) {
            this.selectedIndex = this._validSelectedIndex;
            this.tabHeader.setActiveState(this.selectedIndex);
            this.tabHeader.setIndexValue();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabGroupComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTabGroupComponent, isStandalone: true, selector: "windmill-tab-group", inputs: { shadow: "shadow", noBorder: "noBorder", separatedHeader: "separatedHeader", separatedHeaderPadding: "separatedHeaderPadding", hasPadding: "hasPadding" }, outputs: { selectedTabChange: "selectedTabChange" }, queries: [{ propertyName: "tabs", predicate: WindmillTabComponent }], viewQueries: [{ propertyName: "tabHeader", first: true, predicate: WindmillTabHeaderComponent, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div\r\n  class=\"windmill-tab-group\"\r\n  [class.has-shadow]=\"shadow\"\r\n  [class.has-border]=\"hasBorderClass\"\r\n  [class.has-separated-header]=\"separatedHeader\"\r\n  [class.separated-header-small-padding]=\"hasSmallSeparatedHeaderPadding\">\r\n  <windmill-tab-header\r\n    [tabs]=\"tabs\"\r\n    [theme]=\"theme\"\r\n    [selectedIndex]=\"selectedIndex\"\r\n    [alignArrows]=\"alignArrows\"\r\n    [disableContentAnimation]=\"disableContentAnimation\"\r\n    [ariaLabelTabList]=\"ariaLabelTabList\"\r\n    [ariaLabelledbyTabList]=\"ariaLabelledbyTabList\"\r\n    [ariaDescriptionTabList]=\"ariaDescriptionTabList\"\r\n    [ariaDescribedbyTabList]=\"ariaDescribedbyTabList\"\r\n    (focusChange)=\"onFocusChange($event)\"\r\n    (selectedIndexChange)=\"onSelectedIndexChange($event)\"\r\n    (removeTab)=\"onRemoveTab($event)\">\r\n    <ng-content select=\"[tab-group-buttons]\"></ng-content>\r\n  </windmill-tab-header>\r\n  <windmill-tab-body [hasPadding]=\"hasPadding\">\r\n    <ng-content></ng-content>\r\n  </windmill-tab-body>\r\n</div>\r\n", dependencies: [{ kind: "component", type: WindmillTabHeaderComponent, selector: "windmill-tab-header", inputs: ["tabs"] }, { kind: "component", type: WindmillTabBodyComponent, selector: "windmill-tab-body", inputs: ["hasPadding"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-tab-group', encapsulation: ViewEncapsulation.None, imports: [WindmillTabHeaderComponent, WindmillTabBodyComponent], standalone: true, template: "<div\r\n  class=\"windmill-tab-group\"\r\n  [class.has-shadow]=\"shadow\"\r\n  [class.has-border]=\"hasBorderClass\"\r\n  [class.has-separated-header]=\"separatedHeader\"\r\n  [class.separated-header-small-padding]=\"hasSmallSeparatedHeaderPadding\">\r\n  <windmill-tab-header\r\n    [tabs]=\"tabs\"\r\n    [theme]=\"theme\"\r\n    [selectedIndex]=\"selectedIndex\"\r\n    [alignArrows]=\"alignArrows\"\r\n    [disableContentAnimation]=\"disableContentAnimation\"\r\n    [ariaLabelTabList]=\"ariaLabelTabList\"\r\n    [ariaLabelledbyTabList]=\"ariaLabelledbyTabList\"\r\n    [ariaDescriptionTabList]=\"ariaDescriptionTabList\"\r\n    [ariaDescribedbyTabList]=\"ariaDescribedbyTabList\"\r\n    (focusChange)=\"onFocusChange($event)\"\r\n    (selectedIndexChange)=\"onSelectedIndexChange($event)\"\r\n    (removeTab)=\"onRemoveTab($event)\">\r\n    <ng-content select=\"[tab-group-buttons]\"></ng-content>\r\n  </windmill-tab-header>\r\n  <windmill-tab-body [hasPadding]=\"hasPadding\">\r\n    <ng-content></ng-content>\r\n  </windmill-tab-body>\r\n</div>\r\n" }]
        }], propDecorators: { shadow: [{
                type: Input
            }], noBorder: [{
                type: Input
            }], separatedHeader: [{
                type: Input
            }], separatedHeaderPadding: [{
                type: Input
            }], hasPadding: [{
                type: Input
            }], selectedTabChange: [{
                type: Output
            }], tabs: [{
                type: ContentChildren,
                args: [WindmillTabComponent]
            }], tabHeader: [{
                type: ViewChild,
                args: [WindmillTabHeaderComponent]
            }] } });

const windmillModules = [CentricIcon2Module, WindmillTooltipModule];
const declaredModuleComponents = [
    WindmillTabBodyComponent,
    WindmillTabComponent,
    WindmillTabContentDirective,
    WindmillTabGroupComponent,
    WindmillTabHeaderComponent,
    WindmillTabLabelDirective
];
const exportedModuleComponents = [
    WindmillTabComponent,
    WindmillTabContentDirective,
    WindmillTabGroupComponent,
    WindmillTabLabelDirective
];
class WindmillTabsModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabsModule, imports: [CommonModule, PortalModule, CentricIcon2Module, WindmillTooltipModule, WindmillTabBodyComponent,
            WindmillTabComponent,
            WindmillTabContentDirective,
            WindmillTabGroupComponent,
            WindmillTabHeaderComponent,
            WindmillTabLabelDirective], exports: [WindmillTabComponent,
            WindmillTabContentDirective,
            WindmillTabGroupComponent,
            WindmillTabLabelDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabsModule, imports: [CommonModule, PortalModule, windmillModules, WindmillTabComponent,
            WindmillTabGroupComponent,
            WindmillTabHeaderComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule, ...windmillModules, ...declaredModuleComponents],
                    exports: [...exportedModuleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillTabComponent, WindmillTabContentDirective, WindmillTabGroupComponent, WindmillTabLabelDirective, WindmillTabsModule };
